/**
 * Serviço de Histórico de Sincronização - JurisPrazo V2
 * Controla última sincronização para evitar duplicatas e gaps
 * Funciona como o SEDEP - sempre sabe quando foi a última busca
 */

import { FirebaseService } from './firebaseService';
import { collection, doc, setDoc, getDoc, query, orderBy, limit, getDocs, where, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

class SyncHistoryService {
  
  /**
   * Salvar registro de sincronização
   */
  static async saveSyncRecord(userId, oabInfo, syncData) {
    try {
      const syncId = `${userId}_${oabInfo.numero}_${oabInfo.seccional}_${Date.now()}`;
      const syncRef = doc(db, 'sync_history', syncId);
      
      const record = {
        id: syncId,
        userId,
        oabNumero: oabInfo.numero,
        oabSeccional: oabInfo.seccional,
        
        // Período sincronizado
        dataInicio: syncData.dataInicio,
        dataFim: syncData.dataFim,
        
        // Resultado da sincronização
        totalComunicacoes: syncData.totalComunicacoes || 0,
        novasComunicacoes: syncData.novasComunicacoes || 0,
        comunicacoesDuplicadas: syncData.comunicacoesDuplicadas || 0,
        comunicacoesProcessadas: syncData.comunicacoesProcessadas || 0,
        
        // Status
        status: syncData.status || 'success', // success, partial, failed
        erro: syncData.erro || null,
        
        // Metadados
        tipoSync: syncData.tipoSync || 'manual', // manual, automatic, recovery
        versaoSistema: '2.0.0',
        duracaoMs: syncData.duracaoMs || 0,
        
        // Timestamps
        iniciadoEm: syncData.iniciadoEm || new Date().toISOString(),
        finalizadoEm: new Date().toISOString(),
        createdAt: serverTimestamp(),
        
        // Dados para controle de próxima sync
        proximaDataInicio: syncData.dataFim, // Próxima sync começa onde esta terminou
      };
      
      await setDoc(syncRef, record);
      
      // Atualizar última sincronização do usuário
      await this.updateUserLastSync(userId, oabInfo, record);
      
      console.log(`✅ Registro de sync salvo: ${syncId}`);
      return record;
      
    } catch (error) {
      console.error('❌ Erro ao salvar registro de sync:', error);
      throw error;
    }
  }
  
  /**
   * Atualizar última sincronização do usuário (para acesso rápido)
   */
  static async updateUserLastSync(userId, oabInfo, syncRecord) {
    try {
      const lastSyncRef = doc(db, 'users', userId, 'last_sync', `${oabInfo.numero}_${oabInfo.seccional}`);
      
      const lastSyncData = {
        oabNumero: oabInfo.numero,
        oabSeccional: oabInfo.seccional,
        ultimaDataSincronizada: syncRecord.dataFim,
        ultimaSincronizacaoEm: syncRecord.finalizadoEm,
        proximaDataInicio: syncRecord.proximaDataInicio,
        totalComunicacoes: syncRecord.totalComunicacoes,
        status: syncRecord.status,
        syncId: syncRecord.id,
        updatedAt: serverTimestamp()
      };
      
      await setDoc(lastSyncRef, lastSyncData, { merge: true });
      
      console.log(`✅ Última sync atualizada para OAB ${oabInfo.numero}/${oabInfo.seccional}`);
      
    } catch (error) {
      console.error('❌ Erro ao atualizar última sync:', error);
      throw error;
    }
  }
  
  /**
   * Obter última sincronização para uma OAB
   */
  static async getLastSync(userId, oabInfo) {
    try {
      const lastSyncRef = doc(db, 'users', userId, 'last_sync', `${oabInfo.numero}_${oabInfo.seccional}`);
      const lastSyncDoc = await getDoc(lastSyncRef);
      
      if (lastSyncDoc.exists()) {
        const data = lastSyncDoc.data();
        console.log(`📅 Última sync OAB ${oabInfo.numero}/${oabInfo.seccional}: ${data.ultimaDataSincronizada}`);
        return data;
      } else {
        console.log(`📅 Nenhuma sync anterior para OAB ${oabInfo.numero}/${oabInfo.seccional}`);
        return null;
      }
      
    } catch (error) {
      console.error('❌ Erro ao buscar última sync:', error);
      return null;
    }
  }
  
  /**
   * Calcular período para próxima sincronização
   */
  static async calculateNextSyncPeriod(userId, oabInfo, tipoSync = 'automatic') {
    try {
      const lastSync = await this.getLastSync(userId, oabInfo);
      const hoje = new Date();
      
      let dataInicio, dataFim;
      
      if (!lastSync) {
        // Primeira sincronização - usar configuração inicial
        const settings = await FirebaseService.getMonitoringSettings(userId);
        if (settings && settings.initialSyncPeriod) {
          dataInicio = settings.initialSyncPeriod.startDate;
          dataFim = settings.initialSyncPeriod.endDate;
        } else {
          // Fallback: últimos 30 dias
          const trintaDiasAtras = new Date(hoje.getTime() - 30 * 24 * 60 * 60 * 1000);
          dataInicio = trintaDiasAtras.toISOString().split('T')[0];
          dataFim = hoje.toISOString().split('T')[0];
        }
        
        console.log(`🆕 Primeira sync OAB ${oabInfo.numero}/${oabInfo.seccional}: ${dataInicio} até ${dataFim}`);
        
      } else {
        // Sincronização incremental
        if (tipoSync === 'recovery') {
          // Sync de recuperação - desde a última data + 1 dia até hoje
          const ultimaData = new Date(lastSync.ultimaDataSincronizada);
          const proximoDia = new Date(ultimaData.getTime() + 24 * 60 * 60 * 1000);
          
          dataInicio = proximoDia.toISOString().split('T')[0];
          dataFim = hoje.toISOString().split('T')[0];
          
          console.log(`🔄 Sync de recuperação OAB ${oabInfo.numero}/${oabInfo.seccional}: ${dataInicio} até ${dataFim}`);
          
        } else {
          // Sync automática - últimos 3 dias (overlap para garantir)
          const tresDiasAtras = new Date(hoje.getTime() - 3 * 24 * 60 * 60 * 1000);
          dataInicio = tresDiasAtras.toISOString().split('T')[0];
          dataFim = hoje.toISOString().split('T')[0];
          
          console.log(`⚡ Sync automática OAB ${oabInfo.numero}/${oabInfo.seccional}: ${dataInicio} até ${dataFim}`);
        }
      }
      
      return {
        dataInicio,
        dataFim,
        isFirstSync: !lastSync,
        isRecovery: tipoSync === 'recovery',
        lastSyncDate: lastSync?.ultimaDataSincronizada || null,
        daysSinceLastSync: lastSync ? this.calculateDaysDiff(lastSync.ultimaDataSincronizada, hoje.toISOString().split('T')[0]) : null
      };
      
    } catch (error) {
      console.error('❌ Erro ao calcular período de sync:', error);
      
      // Fallback seguro
      const hoje = new Date();
      const tresDiasAtras = new Date(hoje.getTime() - 3 * 24 * 60 * 60 * 1000);
      
      return {
        dataInicio: tresDiasAtras.toISOString().split('T')[0],
        dataFim: hoje.toISOString().split('T')[0],
        isFirstSync: true,
        isRecovery: false,
        lastSyncDate: null,
        daysSinceLastSync: null
      };
    }
  }
  
  /**
   * Obter histórico completo de sincronizações
   */
  static async getSyncHistory(userId, options = {}) {
    try {
      const {
        limit: queryLimit = 50,
        oabNumero = null,
        oabSeccional = null,
        startDate = null,
        endDate = null
      } = options;
      
      let q = query(
        collection(db, 'sync_history'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(queryLimit)
      );
      
      // Filtros adicionais
      if (oabNumero) {
        q = query(q, where('oabNumero', '==', oabNumero));
      }
      
      if (oabSeccional) {
        q = query(q, where('oabSeccional', '==', oabSeccional));
      }
      
      const querySnapshot = await getDocs(q);
      const history = [];
      
      querySnapshot.forEach((doc) => {
        history.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      console.log(`📚 ${history.length} registros de sync encontrados`);
      return history;
      
    } catch (error) {
      console.error('❌ Erro ao buscar histórico de sync:', error);
      return [];
    }
  }
  
  /**
   * Obter estatísticas de sincronização
   */
  static async getSyncStats(userId) {
    try {
      const history = await this.getSyncHistory(userId, { limit: 100 });
      
      if (history.length === 0) {
        return {
          totalSyncs: 0,
          totalComunicacoes: 0,
          ultimaSync: null,
          oabsConfiguradas: 0,
          diasSemSync: 0,
          mediaComuncacoesPorSync: 0,
          successRate: 0
        };
      }
      
      const totalSyncs = history.length;
      const totalComunicacoes = history.reduce((acc, sync) => acc + (sync.totalComunicacoes || 0), 0);
      const ultimaSync = history[0]; // Mais recente
      const syncsSucesso = history.filter(sync => sync.status === 'success').length;
      
      // OABs únicas
      const oabsUnicas = new Set();
      history.forEach(sync => {
        oabsUnicas.add(`${sync.oabNumero}/${sync.oabSeccional}`);
      });
      
      // Dias sem sync
      const ultimaData = ultimaSync ? new Date(ultimaSync.finalizadoEm) : new Date(0);
      const hoje = new Date();
      const diasSemSync = Math.floor((hoje - ultimaData) / (1000 * 60 * 60 * 24));
      
      return {
        totalSyncs,
        totalComunicacoes,
        ultimaSync: ultimaSync ? {
          data: ultimaSync.finalizadoEm,
          status: ultimaSync.status,
          comunicacoes: ultimaSync.totalComunicacoes
        } : null,
        oabsConfiguradas: oabsUnicas.size,
        diasSemSync,
        mediaComuncacoesPorSync: totalSyncs > 0 ? Math.round(totalComunicacoes / totalSyncs) : 0,
        successRate: totalSyncs > 0 ? Math.round((syncsSucesso / totalSyncs) * 100) : 0
      };
      
    } catch (error) {
      console.error('❌ Erro ao calcular estatísticas de sync:', error);
      return null;
    }
  }
  
  /**
   * Verificar se usuário precisa de sincronização de recuperação
   */
  static async needsRecoverySync(userId) {
    try {
      const oabs = await FirebaseService.getUserOABs(userId);
      const recoveryNeeded = [];
      
      for (const oab of oabs) {
        const lastSync = await this.getLastSync(userId, oab);
        
        if (!lastSync) {
          // Nunca sincronizou
          recoveryNeeded.push({
            oab,
            reason: 'never_synced',
            daysSince: null
          });
        } else {
          // Verificar se faz muito tempo
          const hoje = new Date();
          const ultimaData = new Date(lastSync.ultimaDataSincronizada);
          const daysDiff = this.calculateDaysDiff(ultimaData.toISOString().split('T')[0], hoje.toISOString().split('T')[0]);
          
          if (daysDiff > 7) { // Mais de 7 dias
            recoveryNeeded.push({
              oab,
              reason: 'too_old',
              daysSince: daysDiff,
              lastSync: lastSync.ultimaDataSincronizada
            });
          }
        }
      }
      
      return recoveryNeeded;
      
    } catch (error) {
      console.error('❌ Erro ao verificar necessidade de recovery sync:', error);
      return [];
    }
  }
  
  /**
   * Calcular diferença em dias entre duas datas
   */
  static calculateDaysDiff(date1, date2) {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const diffTime = Math.abs(d2 - d1);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
  
  /**
   * Limpar histórico antigo (manter apenas últimos 6 meses)
   */
  static async cleanOldHistory(userId) {
    try {
      const seisMesesAtras = new Date();
      seisMesesAtras.setMonth(seisMesesAtras.getMonth() - 6);
      
      const q = query(
        collection(db, 'sync_history'),
        where('userId', '==', userId),
        where('createdAt', '<', seisMesesAtras)
      );
      
      const querySnapshot = await getDocs(q);
      const batch = [];
      
      querySnapshot.forEach((doc) => {
        batch.push(doc.ref);
      });
      
      // Deletar em lotes
      for (const docRef of batch) {
        await docRef.delete();
      }
      
      console.log(`🧹 ${batch.length} registros antigos removidos`);
      return batch.length;
      
    } catch (error) {
      console.error('❌ Erro ao limpar histórico antigo:', error);
      return 0;
    }
  }
}

export default SyncHistoryService;
